/**
 * Account hub — Identity · Preferences · Security in one surface.
 *
 * Replaces the previous three-way split (Account / Profile / Settings)
 * so readers have a single place for “me”.
 */
import {
	Alert,
	Avatar,
	Box,
	Button,
	Chip,
	Divider,
	Stack,
	Tab,
	Tabs,
	Typography,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { ProjectSummaryDTO } from "@flaha/shared-types";

import { BillingPanel } from "../features/account/BillingPanel";
import { PreferencesPanel } from "../features/account/PreferencesPanel";
import { SecurityPanel } from "../features/account/SecurityPanel";
import { useAuth } from "../auth";
import { GlassSurface } from "../layouts/components/GlassSurface";
import { usePageHeader } from "../layouts/PageHeaderContext";
import { getApiClient, getApiClientMode } from "../services/apiClientProvider";

export type AccountTab = "identity" | "preferences" | "security" | "billing";

const TAB_VALUES: AccountTab[] = [
	"identity",
	"preferences",
	"security",
	"billing",
];

function parseTab(raw: string | null): AccountTab {
	if (
		raw === "preferences" ||
		raw === "security" ||
		raw === "identity" ||
		raw === "billing"
	) {
		return raw;
	}
	return "identity";
}

function initials(name: string): string {
	const parts = name.trim().split(/\s+/);
	const head = parts[0]?.[0] ?? "?";
	const tail = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? "" : "";
	return (head + tail).toUpperCase();
}

export function AccountPage() {
	const { user, status, activeOrganization, memberships, actions } = useAuth();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const tab = parseTab(searchParams.get("tab"));
	const apiMode = getApiClientMode();
	const [projects, setProjects] = useState<ProjectSummaryDTO[] | null>(null);
	const [resendState, setResendState] = useState<
		"idle" | "sending" | "sent" | "error"
	>("idle");
	const [resendError, setResendError] = useState<string | null>(null);

	usePageHeader({
		title: "Account",
		subtitle: user
			? `${user.displayName} · identity, preferences & security`
			: "Account",
		breadcrumbs: [
			{ label: "Home", to: "/" },
			{ label: "Dashboard", to: "/dashboard" },
			{ label: "Account" },
		],
	});

	useEffect(() => {
		if (status !== "authenticated") return;
		let cancelled = false;
		getApiClient()
			.listProjects({})
			.then((res) => {
				if (!cancelled) setProjects(res.projects);
			})
			.catch(() => {
				if (!cancelled) setProjects([]);
			});
		return () => {
			cancelled = true;
		};
	}, [status, activeOrganization?.id]);

	if (!user) return null;

	const setTab = (_: unknown, value: AccountTab) => {
		setSearchParams(value === "identity" ? {} : { tab: value }, { replace: true });
	};

	const handleResend = async () => {
		setResendState("sending");
		setResendError(null);
		try {
			await getApiClient().resendVerification();
			await actions.refresh();
			setResendState("sent");
		} catch (err) {
			setResendState("error");
			setResendError(err instanceof Error ? err.message : String(err));
		}
	};

	const sampleCount =
		projects === null ? null : projects.reduce((s, p) => s + p.sampleCount, 0);

	return (
		<Box data-testid="account-hub">
			<GlassSurface intensity="subtle" depth="flat" sx={{ mb: 2, px: 1 }}>
				<Tabs
					value={tab}
					onChange={setTab}
					aria-label="Account sections"
					variant="scrollable"
					scrollButtons="auto"
				>
					{TAB_VALUES.map((value) => (
						<Tab
							key={value}
							value={value}
							label={
								value === "identity"
									? "Identity"
									: value === "preferences"
										? "Preferences"
										: value === "security"
											? "Security"
											: "Billing"
							}
						/>
					))}
				</Tabs>
			</GlassSurface>

			{tab === "billing" ? (
				<BillingPanel />
			) : tab === "identity" ? (
				<Box
					sx={{
						display: "grid",
						gap: 3,
						gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
					}}
				>
					{!user.emailVerified && (
						<Box sx={{ gridColumn: "1 / -1" }}>
							<Alert
								severity="warning"
								action={
									<Button
										color="inherit"
										size="small"
										disabled={resendState === "sending" || resendState === "sent"}
										onClick={() => void handleResend()}
										startIcon={<MarkEmailReadIcon />}
									>
										{resendState === "sent"
											? "Sent"
											: resendState === "sending"
												? "Sending…"
												: "Resend email"}
									</Button>
								}
							>
								Your email is not verified yet. Check your inbox for a FlahaSOIL
								verification link.
								{resendError ? ` ${resendError}` : null}
							</Alert>
						</Box>
					)}

					<GlassSurface intensity="standard" depth="raised" sx={{ p: 0, overflow: "hidden" }}>
						<Box sx={{ px: 2.5, py: 2 }}>
							<Typography component="h2" variant="h6">
								Identity
							</Typography>
						</Box>
						<Divider sx={{ borderColor: "workspace.border" }} />
						<Box sx={{ p: 2.5 }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Avatar
									sx={{
										width: 64,
										height: 64,
										bgcolor: "workspace.green",
										color: "workspace.background",
										fontSize: 24,
										fontWeight: 700,
									}}
								>
									{initials(user.displayName)}
								</Avatar>
								<Box sx={{ minWidth: 0 }}>
									<Typography variant="h6" noWrap>
										{user.displayName}
									</Typography>
									<Typography
										variant="body2"
										sx={{ color: "workspace.textSecondary" }}
										noWrap
									>
										{user.email}
									</Typography>
									<Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
										<Chip label={user.role} size="small" color="primary" variant="outlined" />
										<Chip
											label={user.emailVerified ? "Email verified" : "Email unverified"}
											size="small"
											color={user.emailVerified ? "success" : "warning"}
											variant="outlined"
										/>
									</Stack>
								</Box>
							</Stack>
							<Divider sx={{ my: 2, borderColor: "workspace.border" }} />
							<Button
								color="inherit"
								startIcon={<LogoutIcon />}
								onClick={() => navigate("/logout")}
							>
								Sign out
							</Button>
						</Box>
					</GlassSurface>

					<GlassSurface intensity="standard" depth="raised" sx={{ p: 0, overflow: "hidden" }}>
						<Box sx={{ px: 2.5, py: 2 }}>
							<Typography component="h2" variant="h6">
								Session
							</Typography>
						</Box>
						<Divider sx={{ borderColor: "workspace.border" }} />
						<Box sx={{ p: 2.5 }}>
							<Stack spacing={1.5}>
								<Row
									label="API mode"
									value={apiMode === "real" ? "Live backend" : "Demonstration mode"}
								/>
								<Row label="Status" value="Signed in" />
								<Row label="User id" value={user.id} />
								<Row
									label="Active organization"
									value={activeOrganization?.name ?? "None selected"}
								/>
							</Stack>
						</Box>
					</GlassSurface>

					<GlassSurface
						intensity="standard"
						depth="raised"
						sx={{ p: 0, overflow: "hidden", gridColumn: { md: "1 / -1" } }}
					>
						<Box sx={{ px: 2.5, py: 2 }}>
							<Typography component="h2" variant="h6">
								Platform activity
							</Typography>
							<Typography variant="caption" sx={{ color: "workspace.textMuted" }}>
								Aggregated across projects you can access
							</Typography>
						</Box>
						<Divider sx={{ borderColor: "workspace.border" }} />
						<Box
							sx={{
								p: 2.5,
								display: "grid",
								gap: 2,
								gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
							}}
						>
							<Stat label="Projects" value={projects === null ? null : projects.length} />
							<Stat label="Samples" value={sampleCount} />
							<Stat label="Tests" value="Per project" muted />
							<Stat label="Reports" value="Per project" muted />
						</Box>
					</GlassSurface>

					<GlassSurface
						intensity="standard"
						depth="raised"
						sx={{ p: 0, overflow: "hidden", gridColumn: { md: "1 / -1" } }}
					>
						<Box sx={{ px: 2.5, py: 2 }}>
							<Typography component="h2" variant="h6">
								Memberships
							</Typography>
							<Typography variant="caption" sx={{ color: "workspace.textMuted" }}>
								{memberships.length} organization(s)
							</Typography>
						</Box>
						<Divider sx={{ borderColor: "workspace.border" }} />
						<Box sx={{ p: 2.5 }}>
							{memberships.length === 0 ? (
								<Typography variant="body2" sx={{ color: "workspace.textSecondary" }}>
									You are not a member of any organization yet.
								</Typography>
							) : (
								<Stack spacing={1.5}>
									{memberships.map((m) => (
										<Stack
											key={m.id}
											direction={{ xs: "column", sm: "row" }}
											justifyContent="space-between"
											gap={1}
											sx={{
												p: 1.5,
												borderRadius: 1.25,
												border: "1px solid",
												borderColor: "workspace.border",
											}}
										>
											<Box>
												<Typography variant="body2" sx={{ fontWeight: 600 }}>
													{m.organization?.name ?? m.organizationId}
												</Typography>
												<Typography
													variant="caption"
													sx={{ color: "workspace.textMuted" }}
												>
													{m.organization?.slug}
												</Typography>
											</Box>
											<Stack direction="row" spacing={1} alignItems="center">
												<Chip label={m.role} size="small" color="primary" />
												<Chip label={m.status} size="small" variant="outlined" />
											</Stack>
										</Stack>
									))}
								</Stack>
							)}
						</Box>
					</GlassSurface>
				</Box>
			) : null}

			{tab === "preferences" ? <PreferencesPanel /> : null}
			{tab === "security" ? <SecurityPanel /> : null}
		</Box>
	);
}

function Row({ label, value }: { label: string; value: string }) {
	return (
		<Stack direction="row" justifyContent="space-between" gap={2}>
			<Typography variant="body2" sx={{ color: "workspace.textSecondary" }}>
				{label}
			</Typography>
			<Typography variant="body2" sx={{ textAlign: "right", fontWeight: 500 }}>
				{value}
			</Typography>
		</Stack>
	);
}

function Stat({
	label,
	value,
	muted,
}: {
	label: string;
	value: number | string | null;
	muted?: boolean;
}) {
	return (
		<Box>
			<Typography variant="caption" sx={{ color: "workspace.textMuted" }}>
				{label}
			</Typography>
			<Typography
				variant="h5"
				sx={{
					color: muted ? "workspace.textMuted" : "workspace.textPrimary",
					fontWeight: 600,
				}}
			>
				{value ?? "—"}
			</Typography>
		</Box>
	);
}
